import { useCallback, useEffect, useMemo, useState } from 'react'
import { CornerDownLeft, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import RowActions from '../../components/RowActions'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { deleteCategory, listCategories, saveCategory } from './incidentsService'
import { incidentsStrings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'

const BLANK = { id: 0, nameAr: '', nameEn: '', parentId: '' }

/**
 * إعدادات الحوادث السيبرانية — the feature's switch, and the categories a reporter picks from.
 *
 * A settings tab of its own rather than a drawer off a button: the switch and the categories
 * are one decision — **the feature cannot be switched on until at least one category exists**
 * (`update-report-enabled` refuses and stores `false`) — so they belong on one screen, the
 * switch first, the categories under it as two cards side by side: what you are adding or
 * editing, and what is already there.
 *
 * Categories nest one level. A category with a parent is a sub-category; there is no third
 * tier, so the parent select offers only the top-level ones.
 *
 * @param {boolean} enabled
 * @param {(on: boolean) => void} onToggle  saves at once — a system switch, not a form field
 * @param {() => void} [onChanged]  the categories changed; the report list re-reads them
 */
export default function IncidentSettingsPanel({ enabled, onToggle, onChanged }) {
  const toast = useToast()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(BLANK)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)

  const load = useCallback(
    () =>
      listCategories()
        .then((data) => {
          setRows(data)
          setLoading(false)
        })
        .catch((error) => {
          setRows([])
          setLoading(false)
          toast.error(serverDetail(error?.message) || S.fail.settings)
        }),
    [toast],
  )

  useEffect(() => {
    load()
  }, [load])

  /* Top level first, each with its children right under it — the same order the
     reporter's category picker shows, so what is edited here is what they will see. Flat,
     because a table is rows; the child carries its parent's name to say where it sits. */
  const ordered = useMemo(() => {
    const tops = rows.filter((row) => row.parentId == null)
    return tops.flatMap((top) => [
      { ...top, parentName: '' },
      ...rows.filter((row) => row.parentId === top.id).map((child) => ({ ...child, parentName: top.name })),
    ])
  }, [rows])

  const parentOptions = useMemo(
    () =>
      rows
        .filter((row) => row.parentId == null && row.id !== form.id)
        .map((row) => ({ value: String(row.id), label: row.name })),
    [rows, form.id],
  )

  const edit = (row) => {
    setForm({
      id: row.id,
      nameAr: row.nameAr,
      nameEn: row.nameEn,
      parentId: row.parentId == null ? '' : String(row.parentId),
    })
    setEditing(true)
  }

  const reset = () => {
    setForm(BLANK)
    setEditing(false)
  }

  const submit = async (event) => {
    event?.preventDefault()
    if (!form.nameAr.trim() || !form.nameEn.trim()) return toast.error(S.namesRequired)
    setSaving(true)
    const result = await saveCategory(form)
    setSaving(false)
    if (!result.ok) return toast.error(serverDetail(result.message) || S.fail.saveCategory)
    toast.success(serverText(result.message) || S.saved)
    reset()
    setLoading(true)
    await load()
    onChanged?.()
    return undefined
  }

  const confirmDelete = async () => {
    const target = pendingDelete
    setPendingDelete(null)
    const result = await deleteCategory(target.id)
    if (!result.ok) return toast.error(serverDetail(result.message) || S.fail.deleteCategory)
    toast.success(S.deleted)
    setLoading(true)
    await load()
    onChanged?.()
    return undefined
  }

  const columns = useMemo(
    () => [
      /* Nothing sorts: the rows are in tree order, and a sort would pull the children
         away from their parents. */
      {
        key: 'nameAr',
        header: S.category.nameAr,
        sortable: false,
        render: (item) => (
          <span className={`inc-cat__cell${item.parentId == null ? '' : ' inc-cat__cell--child'}`}>
            {/* A sub-category is stepped in under its parent, with the turn a tree draws. */}
            {item.parentId != null && (
              <CornerDownLeft className="inc-cat__turn" size={14} strokeWidth={1.75} aria-hidden="true" />
            )}
            <bdi className={item.parentId == null ? 'inc-cat__top' : undefined}>{item.nameAr}</bdi>
          </span>
        ),
      },
      {
        key: 'nameEn',
        header: S.category.nameEn,
        sortable: false,
        render: (item) => (
          <bdi dir="ltr" className={item.parentId == null ? 'inc-cat__top' : undefined}>
            {item.nameEn}
          </bdi>
        ),
      },
      {
        key: 'parentName',
        header: S.category.parent,
        sortable: false,
        render: (item) => (item.parentName ? <bdi>{item.parentName}</bdi> : <span className="inc-cat__none">—</span>),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (item) => (
          <RowActions
            actions={[
              { id: 'edit', icon: Pencil, label: S.edit, onClick: () => edit(item) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.delete,
                tone: 'danger',
                onClick: () => setPendingDelete(item),
              },
            ]}
          />
        ),
      },
    ],
    // `edit` and `setPendingDelete` are stable for the panel's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <>
      <SwitchField feature checked={enabled} onChange={onToggle} label={S.featureSwitch} />

      <div className="inc-settings">
        <Card
          title={editing ? S.categoryEdit : S.categoryForm}
          subtitle={S.categoriesHint}
          footer={
            <>
              <Button type="submit" form="inc-category" busy={saving}>
                {!editing && <Plus size={16} strokeWidth={1.75} />}
                {editing ? S.apply : S.addNew}
              </Button>
              {editing && (
                <Button variant="ghost" onClick={reset}>
                  {S.cancel}
                </Button>
              )}
            </>
          }
        >
          <form id="inc-category" className="inc-settings__form" onSubmit={submit}>
            <FormRow label={S.category.nameAr}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.nameAr}
                  onChange={(value) => setForm((f) => ({ ...f, nameAr: value }))}
                />
              )}
            </FormRow>
            <FormRow label={S.category.nameEn}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.nameEn}
                  onChange={(value) => setForm((f) => ({ ...f, nameEn: value }))}
                />
              )}
            </FormRow>
            <FormRow label={S.category.parent} hint={S.category.parentHint}>
              {(id) => (
                <Select
                  id={id}
                  value={form.parentId}
                  onChange={(value) => setForm((f) => ({ ...f, parentId: value }))}
                  options={parentOptions}
                  placeholder={S.category.noParent}
                />
              )}
            </FormRow>
          </form>
        </Card>

        <Card title={S.category.existing} className="inc-settings__table">
          <DataTable
            columns={columns}
            rows={ordered}
            loading={loading}
            controls={false}
            paginate={false}
            initialSort={{ key: null }}
            getRowId={(item) => item.id}
            emptyMessage={S.category.empty}
          />
        </Card>
      </div>

      {/* Deleting takes every sub-category with it, and the API says so only by refusing when
          one of them is in use. The confirm says it up front. */}
      <ConfirmDialog
        open={pendingDelete != null}
        message={S.category.deleteConfirm}
        confirmLabel={S.delete}
        cancelLabel={S.cancel}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
